// Renderer bootstrap: app, atlas, scenes, fx, EventPlayer, and the input controller.
import { ALL_SPRITES } from '../art/sprites';
import { initAtlas } from '../art/rasterize';
import type { Command } from '../sim/types';
import { dispatch, getGame, getUI, store } from '../state/store';
import { createApp, type GameApp } from './app';
import { BonusViews } from './bonus/BonusViews';
import { Fx } from './fx/fx';
import { Particles } from './fx/Particles';
import { Popups } from './fx/Popups';
import { juice } from './juice';
import { MachineScene } from './machine/MachineScene';
import { WebScene } from './scenes/WebScene';
import { clock } from './timeline/clock';
import { EventPlayer } from './timeline/EventPlayer';
import { HandController } from './hand';

export interface Renderer {
  ga: GameApp;
  machine: MachineScene;
  web: WebScene;
  player: EventPlayer;
  bonus: BonusViews;
  hand: HandController;
}

let renderer: Renderer | null = null;
export const getRenderer = () => renderer;

/** Player intents from any input (lever, button, keyboard). */
export const controller = {
  pull(perfect = false, fromLever = false): void {
    const g = getGame();
    const ui = getUI();
    if (!renderer || !g.run || ui.screen !== 'machine') return;
    if (g.run.phase !== 'ready' && g.run.phase !== 'nudge') {
      if (renderer.player.busy) renderer.player.skipAll();
      return;
    }
    if (ui.stage !== 'play') return;
    if (renderer.player.busy) renderer.player.skipAll();
    renderer.player.leverPulled = fromLever;
    const muscle = g.meta.settings.muscleMemory;
    dispatch({ t: 'PULL', perfect: perfect || muscle, assisted: muscle && !perfect });
  },
  /** Gameplay commands wait for the timeline; they fast-forward it first. */
  act(cmd: Command): boolean {
    if (renderer?.player.busy) renderer.player.skipAll();
    return dispatch(cmd);
  },
  skip(): void {
    renderer?.player.skipAll();
  },
  toggleHand(): void {
    renderer?.hand.toggle();
  },
};

export async function startRenderer(stageEl: HTMLElement, canvasHost: HTMLElement): Promise<Renderer> {
  const ga = await createApp(stageEl, canvasHost);
  // Keep the stage passive: in Pixi 8 a static ancestor makes every descendant hit-testable.
  ga.overlay.eventMode = 'none';
  initAtlas(ALL_SPRITES);

  const fx = new Fx(ga);
  const particles = new Particles();
  const popups = new Popups();
  const machine = new MachineScene(ga);
  const web = new WebScene(ga);
  const bonus = new BonusViews(popups, fx, ga.app.ticker);
  popups.layer.eventMode = 'none';
  particles.layer.eventMode = 'none';
  ga.world.addChild(machine, ...bonus.layers, web, popups.layer, particles.layer);
  // particles and popups belong above everything in the world
  const player = new EventPlayer(ga, machine, fx, particles, popups);
  player.hooks = {
    bonusOpen: () => bonus.open(),
    bonusUpdate: () => bonus.update(),
    bonusClose: (p, v) => bonus.close(p, v),
    nodeBought: (id) => web.nodeBought(id),
  };
  machine.onPullRequest = (perfect, fromLever) => controller.pull(perfect, fromLever);

  const applyScreen = () => {
    const { ui, game } = store.getState();
    machine.visible = ui.screen === 'machine';
    bonus.sync(ui.screen === 'machine' && ui.stage === 'bonus' && !!game.run?.bonus);
    web.visible = ui.screen === 'web';
    clock.speed = juice.speed();
    if (web.visible) web.sync(game);
    if (machine.visible && game.run && !player.busy) {
      machine.sync(game);
      player.applyMood(game);
    }
  };
  let prev = store.getState();
  store.subscribe((s) => {
    if (s.ui.screen !== prev.ui.screen || s.ui.stage !== prev.ui.stage || s.game !== prev.game || s.ui.selectedNode !== prev.ui.selectedNode || s.ui.nudgeHover !== prev.ui.nudgeHover || s.ui.padReel !== prev.ui.padReel || s.ui.padActive !== prev.ui.padActive) {
      const reopen = s.ui.screen === 'machine' && s.ui.stage === 'bonus' && prev.ui.screen !== 'machine' && !!s.game.run?.bonus;
      prev = s;
      applyScreen();
      if (reopen) bonus.open();
    }
  });
  applyScreen();
  if (store.getState().game.run?.phase === 'bonus') bonus.open();

  const hand = new HandController(player, machine);
  renderer = { ga, machine, web, player, bonus, hand };
  return renderer;
}
